import { useQueryClient } from '@tanstack/react-query';

import { storeCatalogKeys } from './store-catalog-keys';

export interface AdjacentStoreProducts {
  previous: string | null;
  next: string | null;
}

function rowIds(data: unknown): string[] | null {
  if (typeof data !== 'object' || data === null || !('items' in data)) return null;
  const { items } = data;
  if (!Array.isArray(items)) return null;
  return items.map((item: { id: string }) => item.id);
}

/**
 * The rows around this one in the store list most recently fetched that holds it, from the cache
 * only, so opening a product directly asks for nothing and has no neighbours. Null then, and for
 * a list of this row alone (a lookup, not a list someone was walking through).
 */
export function useAdjacentStoreProducts(
  storeId: string,
  storeProductId: string,
): AdjacentStoreProducts | null {
  const queryClient = useQueryClient();
  const cached = queryClient
    .getQueryCache()
    .findAll({ queryKey: storeCatalogKeys.lists(storeId) })
    .toSorted((a, b) => b.state.dataUpdatedAt - a.state.dataUpdatedAt);
  for (const query of cached) {
    const ids = rowIds(query.state.data);
    if (!ids || ids.length < 2) continue;
    const index = ids.indexOf(storeProductId);
    if (index === -1) continue;
    return { previous: ids[index - 1] ?? null, next: ids[index + 1] ?? null };
  }
  return null;
}
